/**
 * Internal redline edit shapes.
 *
 * - `RedlineEdit` is the input shape consumed by the slate `redline-transforms`
 *   apply pipeline.
 * - `CodeBlockRedlineItem` (and its discriminated-union members) is the wire
 *   format the server streams as JSONL inside a `jsonl-redline-edit` code
 *   block and the client parses to render redline rows.
 *
 * The LLM-facing output format lives in `./redline-llm-output.ts`.
 */

import { z } from 'zod';

import { ExtractedTextSegmentSchema } from '../extract-paragraphs/shared/types';
import type { VisualDiffSegment } from '../slate-api/transforms/diff-visualization/types';
import { assertTypeEqual } from '../util';

// =============================================================================
// In-Paragraph Action Schemas
// =============================================================================

const InParagraphModifyAction = z.object({
  action: z.literal('modify').describe('Make a new modification to the paragraph'),
  content: z.string().describe('Content text with range markers around the modified portion'),
  reasoning: z.string().describe('Why this modification is being made'),
});

export const InParagraphEditDetailsSchema = z.object({
  actions: z.array(InParagraphModifyAction),
  category: z.literal('in-paragraph'),
  documentId: z.string().describe('Document ID being edited'),
  locationAnchor: z.string().describe('Unique identifier for the paragraph'),
});

export const EditDetailsSchema = InParagraphEditDetailsSchema;

export const RedlineEditSchema = z.object({
  edit: EditDetailsSchema,
});

/**
 * This schema matches the DiffSegment type from the slate-api/transforms/layered-diffs package.
 */
export const VisualDiffSegmentSchema = z.object({
  newDelete: z.optional(z.literal(true)).describe('New delete flag'),
  newInsert: z.optional(z.literal(true)).describe('New insert flag'),
  oldDelete: z.optional(z.literal(true)).describe('Old delete flag'),
  oldInsert: z.optional(z.literal(true)).describe('Old insert flag'),
  text: z.string().describe('Text of the segment'),
});

type VisualDiffSegmentFromSchema = z.infer<typeof VisualDiffSegmentSchema>;

assertTypeEqual<VisualDiffSegmentFromSchema, VisualDiffSegment>(true);

export type RedlineEdit = z.infer<typeof RedlineEditSchema>;

/**
 * The schema for a redline edit inside of a Markdown code block.
 *
 * The schema has been extended to include a groupId for the edit. This is used
 * to provide a unique identifier for the edit.
 *
 * Includes `type: 'redline-edit'` so it can be parsed as part of a discriminated
 * union alongside {@link CodeBlockRedlineEditErrorSchema} in the JSONL stream.
 */
export const CodeBlockRedlineEditSchema = InParagraphEditDetailsSchema.extend({
  actions: z.array(
    InParagraphModifyAction.extend({
      groupId: z.string().min(1).describe('Group ID for the edit'),
      visualDiffSegments: z
        .array(VisualDiffSegmentSchema)
        .describe('Preview segments for the edit'),
    })
  ),
  segments: z.array(ExtractedTextSegmentSchema).describe('Text segments of the paragraph'),
  textHash: z.string().describe('MD5 hash of the paragraph text at the time of edit generation'),
  type: z.literal('redline-edit'),
}).describe('The schema for a redline edit inside of a Markdown code block');

export type CodeBlockRedlineEdit = z.infer<typeof CodeBlockRedlineEditSchema>;

/**
 * Emitted by the server as a sibling JSONL line in the redline code block when
 * a redline edit cannot be applied to the document (paragraph not found,
 * transform threw, etc.). Rendered by the client as a non-actionable error row
 * within the redline list so the user sees that work was attempted but failed
 * for that paragraph.
 */
export const CodeBlockRedlineEditErrorSchema = z.object({
  documentId: z.string().describe('Document ID the edit targeted'),
  groupId: z.string().min(1).describe('Group ID generated for the failed edit'),
  locationAnchor: z.string().describe('Paragraph location anchor that could not be edited'),
  message: z.string().describe('Human-readable explanation of the failure'),
  type: z.literal('redline-edit-error'),
});

export type CodeBlockRedlineEditError = z.infer<typeof CodeBlockRedlineEditErrorSchema>;

/**
 * Emitted by the server as a JSONL line in the redline code block when an
 * `insert` suggestion has been applied to the document: new content inserted
 * before or after an existing paragraph as a suggested edit, wrapped in a
 * single suggestion-marker pair identified by `groupId`.
 */
export const CodeBlockRedlineInsertSchema = z.object({
  /**
   * The slice of the reference paragraph to show beside the insertion so the
   * reader sees where the new content lands, already trimmed from the far end
   * with an ellipsis when the paragraph is too long to show whole. Absent when
   * the anchor has no text to show — row inserts, blank anchor paragraphs.
   */
  anchorPreview: z
    .string()
    .optional()
    .describe('Reference paragraph text to render beside the insertion, trimmed for display'),
  documentId: z.string().describe('Document ID the content was inserted into'),
  groupId: z.string().min(1).describe('Group ID for the insertion'),
  html: z
    .string()
    .describe(
      'HTML of the inserted content, rendered (sanitized on the client) as the card preview'
    ),
  locationAnchor: z
    .string()
    .describe('Location anchor of the paragraph the insertion is relative to'),
  position: z
    .enum(['before', 'after'])
    .describe('Whether the content was inserted before or after the anchor paragraph'),
  reasoning: z.string().describe('Why the content was inserted'),
  /**
   * Dependency fingerprint: MD5 of the anchor paragraph's text at generation
   * time, so a deferred-materialization consumer can detect that the anchor
   * changed. Absent when the anchor has no text dependency (row inserts).
   */
  textHash: z
    .string()
    .optional()
    .describe('MD5 hash of the anchor paragraph text at the time of edit generation'),
  type: z.literal('redline-insert'),
});

export type CodeBlockRedlineInsert = z.infer<typeof CodeBlockRedlineInsertSchema>;

/**
 * Emitted by the server as a JSONL line in the redline code block when a
 * `remove` suggestion has been applied to the document: every block from the
 * start paragraph through the end paragraph marked as a suggested deletion,
 * wrapped in a single suggestion-marker pair identified by `groupId`.
 */
export const CodeBlockRedlineRemoveSchema = z.object({
  documentId: z.string().describe('Document ID the content was removed from'),
  endLocationAnchor: z
    .string()
    .describe('Location anchor of the last removed paragraph (equals locationAnchor for one)'),
  groupId: z.string().min(1).describe('Group ID for the removal'),
  html: z
    .string()
    .describe(
      'HTML preview of the removed content, keeping table structure; rendered (sanitized on the client) struck-through as the card preview'
    ),
  locationAnchor: z
    .string()
    .describe('Location anchor of the first removed paragraph — used for navigation'),
  reasoning: z.string().describe('Why the content was removed'),
  /**
   * Dependency fingerprint: MD5 of the removed paragraphs' texts (newline
   * joined, in document order) at generation time, so a
   * deferred-materialization consumer can detect that the targets changed.
   * Absent when the removal has no text dependency — row removals, and
   * whole-table removals, which are intentional regardless of the table's
   * contents.
   */
  textHash: z
    .string()
    .optional()
    .describe('MD5 hash of the removed paragraph texts at the time of edit generation'),
  type: z.literal('redline-remove'),
});

export type CodeBlockRedlineRemove = z.infer<typeof CodeBlockRedlineRemoveSchema>;

/**
 * A `note` is the editor subagent's general user-facing text channel: once
 * inside the subagent, edits and errors are its only other outputs, so a note
 * is the one way it can say anything that is neither an edit nor an error. For
 * example, when it declines part of a request — "no edits were made because…"
 * (unsupported change, target not found, no edit needed) — but a note can carry
 * any message the agent needs to surface, so its use is not limited to that
 * case. Rendered as a neutral, non-actionable card; nothing is applied to the
 * document.
 */
export const CodeBlockRedlineNoteSchema = z.object({
  documentId: z.string().describe('Document ID the note is about'),
  groupId: z.string().min(1).describe('Group ID for the note (stream dedup only)'),
  text: z.string().describe('The user-facing note text'),
  type: z.literal('redline-note'),
});

export type CodeBlockRedlineNote = z.infer<typeof CodeBlockRedlineNoteSchema>;

/**
 * Discriminated union of JSONL lines emitted in a `jsonl-redline-edit` block.
 */
export const CodeBlockRedlineItemSchema = z.discriminatedUnion('type', [
  CodeBlockRedlineEditSchema,
  CodeBlockRedlineEditErrorSchema,
  CodeBlockRedlineInsertSchema,
  CodeBlockRedlineRemoveSchema,
  CodeBlockRedlineNoteSchema,
]);

export type CodeBlockRedlineItem = z.infer<typeof CodeBlockRedlineItemSchema>;
